import { useRef, useState, useEffect } from "react";
import { Upload, Download, Crown } from "lucide-react";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import { uploadFileRequest } from "@/store/slices/fileUploadSlice";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";
import CustomTooltip from "@/components/ui/custom-tooltip";

export function FileUpload() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);
  const { isUploading, uploadedFileId } = useAppSelector((state) => state.fileUpload);
  const subscription = useSubscription();

  useEffect(() => {
    if (uploadedFileId && !isUploading) {
      navigate(`/file-details/${uploadedFileId}`);
    }
  }, [uploadedFileId, isUploading, navigate]);

  const handleUploadClick = () => {
    if (!subscription.canUploadProducts() || !subscription.hasQueryCapacity() || !subscription.hasDashboardCapacity()) {
      subscription.showSubscriptionModal("file upload feature");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "text/csv",
    ];

    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload an Excel or CSV file",
        variant: "destructive",
      });
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: "binary" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            throw new Error("The file is empty.");
          }

          const products = jsonData.map((row: any) => ({
            title_en: row.title_en?.toString() || "",
            ...(row.title_ar ? { title_ar: row.title_ar.toString() } : {}),
          }));

          dispatch(uploadFileRequest(products));
          
          toast({
            title: "File upload started",
            description: "Your file is being processed",
          });
        } catch (error) {
          toast({
            title: "Error",
            description: error instanceof Error ? error.message : "Failed to process file",
            variant: "destructive",
          });
        }
      };

      reader.readAsBinaryString(file);
    } finally {
      event.target.value = "";
    }
  };

  const downloadTemplate = async () => {
    try {
      setIsDownloading(true);
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet([], { header: ["title_en"] });

      XLSX.utils.sheet_add_json(ws, [{ title_en: "Example Product Title" }], {
        skipHeader: true,
        origin: "A2",
      });

      XLSX.utils.book_append_sheet(wb, ws, "Products Template");
      XLSX.writeFile(wb, "products_template.xlsx");

      toast({
        title: "Success",
        description: "Template downloaded successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download template",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const canUpload = subscription.canUploadProducts() && subscription.hasQueryCapacity() && subscription.hasDashboardCapacity();

  return (
    <div className="flex items-center gap-4">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={handleFileUpload}
        className="hidden"
      />

      {!canUpload ? (
        <CustomTooltip content="Upgrade your plan to upload files">
          <Button
            onClick={handleUploadClick}
            variant="outline"
            className="relative"
          >
            <Crown className="mr-2 h-4 w-4 text-yellow-500" />
            {isUploading ? "Uploading..." : "Upload File"}
          </Button>
        </CustomTooltip>
      ) : (
        <Button
          onClick={handleUploadClick}
          variant="default"
          className="relative"
        >
          <Upload className="mr-2 h-4 w-4" />
          {isUploading ? "Uploading..." : "Upload File"}
        </Button>
      )}

      <Button
        variant={"outline"}
        onClick={downloadTemplate}
        disabled={isDownloading}
        className={"relative"}
      >
        <Download className="mr-2 h-4 w-4" />
        {isDownloading ? "Downloading..." : "Download Template"}
      </Button>
    </div>
  );
}
